/* eslint-disable no-console */
import i18n from 'i18next'
import { generatePath } from 'react-router-dom'

import { actions as commentsActions } from '@containers/comments/comments-slice'
import { actions as metadataActions } from '@containers/metadata/metadata-slice'
import { getApi } from '@core/api'
import ErrorHandler from '@core/api/ErrorHandler'
import type { V1ItemDataOutput } from '@core/api/generated'
import { ITEM_SET } from '@core/constants/content-type'
import { AUTHOR_ITEM_ROUTE } from '@core/constants/routes'
import { addSnack } from '@core/snack/snack-state'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { Item, ItemMap, ItemRoot, StateLoaded } from './author-cloze-types'
import * as utils from './author-cloze-utils'
import { toClozeContentRefresh, toRawClozeItemSet } from './cloze-transforms'

/**
 * Fetch initial item data
 */
export const fetchItemSet = createAsyncThunk<Partial<StateLoaded>, string>(
  'authorCloze/fetchItemSet',
  async (id, { extra, dispatch }) => {
    try {
      const api = getApi()
      const { data } = await api.getV1Item({
        path: { item_id: id },
        query: { include_data: true },
      })

      if (!data?.itemData) {
        extra.navigate('/404')
        return { notFound: true }
      }

      const childrenData = (data.childrenStates ?? [])
        .map((c) => c.itemData)
        .filter((d): d is V1ItemDataOutput => d != null)

      const rawItemSet = toRawClozeItemSet(data.itemData, childrenData)

      const { children, root } = utils.normalizeItemSet(rawItemSet)
      const items = { ...children, root } as ItemMap

      if (root.aiModel.type !== ITEM_SET) {
        const path = generatePath(AUTHOR_ITEM_ROUTE, { itemId: id })
        extra.navigate(path, { replace: true })
        return {}
      }

      dispatch(
        metadataActions.setInitialValues({
          itemId: root.id,
          originalSubmodels: root.originalContent.submodels,
          submodels: root.currentContent.submodels,
          isCloze: true,
          externalMetadata: root.currentContent.externalMetadata,
        }),
      )

      const { cloze_ids: clozeIds = [], mcq_ids: mcqIds = [] } = root.currentContent.content

      let startIndex = 1

      for (const itemId of [...mcqIds, ...clozeIds]) {
        const item = items[itemId]

        if (!item) {
          console.error('item not found', itemId)
          continue
        }

        items[itemId] = { ...item, index: startIndex }
        startIndex += 1
      }

      if (items) {
        dispatch(
          commentsActions.set({
            itemId: items.root.id,
            comments: items.root.currentContent.comments,
          }),
        )
      }

      return { initialized: true, items }
    } catch (error) {
      console.error(error)
      ErrorHandler(error)

      addSnack({
        message: i18n.t('errors.item_fetch', { ns: 'author' }),
        severity: 'error',
      })

      throw error
    }
  },
)

/**
 * Refetch an item root content after some status change.
 * If the response does not include the stimulus, wait some time and try again
 * (content versions are populated by an async dramatiq task after status flips).
 */
interface FetchItemContentParams {
  isRoot: boolean
  id: string
}

const wait = (t: number) => new Promise((r) => setTimeout(r, t))

const tryFetchRoot = async (
  makeRequest: () => Promise<V1ItemDataOutput>,
  n = 1,
): Promise<ItemRoot> => {
  console.log('try fetch ->', n)
  if (n > 20) {
    throw new Error('Could not fetch root stimulus')
  }

  const itemData = await makeRequest()
  const refresh = toClozeContentRefresh(itemData)

  // Root content has `stimulus`; child content does not. The refresh type is
  // child-shaped by default — read through a permissive view here.
  const rootContent = refresh.currentContent[0]?.content as { stimulus?: string } | undefined

  if (!rootContent?.stimulus) {
    await wait(200 * n)
    return tryFetchRoot(makeRequest, n + 1)
  }

  // `refresh` is the array-wrapped legacy refresh shape; we unwrap into the
  // ItemRoot shape consumed by the slice. ItemRoot omits identifier/createdAt/
  // etc. fields still present on `refresh`, so the structural fit isn't exact
  // — the runtime shape produced here is the same shape the legacy GraphQL
  // path produced and the slice has been reading without complaint.
  const rootItem: ItemRoot = {
    ...refresh,
    currentContent: {
      content: refresh.currentContent[0]?.content,
      submodels: refresh.currentContent[0]?.submodels,
      qualityMetrics: refresh.currentContent[0]?.qualityMetrics,
    },
    savedContent: {
      content: refresh.savedContent[0]?.content,
    },
    originalContent: {
      submodels: refresh.originalContent[0]?.submodels,
    },
  } as unknown as ItemRoot

  return rootItem
}

export const fetchItemContent = createAsyncThunk<Item, FetchItemContentParams>(
  'authorCloze/fetchItemContent',
  async ({ isRoot, id }) => {
    const api = getApi()
    try {
      const makeRequest = async (): Promise<V1ItemDataOutput> => {
        const { data } = await api.getV1Item({
          path: { item_id: id },
          query: { include_data: true },
        })

        if (!data?.itemData) {
          throw new Error(`Item ${id} not found`)
        }

        return data.itemData
      }

      // tryFetchRoot returns ItemRoot (an item-set root); fetchItemContent's
      // generic return type is Item (cloze child | mcq child). The slice's
      // fetchItemContent.fulfilled reducer branches on `state.items.root.id`,
      // so an ItemRoot payload in the root branch is correct at runtime.
      if (isRoot) return tryFetchRoot(makeRequest) as unknown as Item

      const itemData = await makeRequest()
      const refresh = toClozeContentRefresh(itemData)

      // Same legacy-shape unwrap as tryFetchRoot but for a single child. Item
      // (ItemCloze | ItemMCQ) typing is narrower than the produced object; the
      // slice reads `currentContent.content` / `submodels` which match.
      const item = {
        ...refresh,
        currentContent: {
          content: refresh.currentContent[0]?.content,
          submodels: refresh.currentContent[0]?.submodels,
          qualityMetrics: refresh.currentContent[0]?.qualityMetrics,
        },
        savedContent: {
          content: refresh.savedContent[0]?.content,
        },
        originalContent: {
          submodels: refresh.originalContent[0]?.submodels,
        },
      } as unknown as Item

      return item
    } catch (error) {
      console.error(error)
      ErrorHandler(error)

      addSnack({
        message: i18n.t('errors.item_fetch', { ns: 'author' }),
        severity: 'error',
      })

      throw error
    }
  },
)
